import { useState, useEffect, useMemo } from "react";
import LoadingBar from "@components/ui/LoadingBar";
import Icon1 from "@assets/icons/Icon";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { useOrdreStore } from "@store/ordreStore";
import { useVehiculesStore } from "@store/vehiculesStore";
import { useMessagerieStore } from "@store/messagerieStore";
import { navigateTo } from "context/navigation.service";
import BookingWidget from "@components/BookingWidget";

// --- Campaigns (no backend yet — keep static) ---

const mockCampaigns = [
  { id: "1", title: "TAKATA", description: "Les véhicules Peugeot sont concernés par une campagne." },
];

// --- Payments chart (no backend yet — keep static) ---

const mockPaymentsData = [
  15000, 18000, 22000, 25000, 28000, 32000, 35000, 38000, 42000, 48400, 45000, 40000,
];
const months = ["JAN", "FEV", "MAR", "AVR", "MAI", "JUI", "JLT", "AOU", "SEP", "OCT", "NOV", "DEC"];

// --- Status label mapping ---

const STATUS_LABELS: Record<string, { label: string; type: "default" | "info" | "warning" }> = {
  a_preparer: { label: "À préparer", type: "default" },
  commande: { label: "Commande", type: "warning" },
  pret: { label: "Prêt", type: "info" },
  reception: { label: "Réception", type: "default" },
  en_cours: { label: "En cours", type: "info" },
  devis: { label: "Devis", type: "warning" },
  accord: { label: "Accord", type: "info" },
  fin_de_travaux: { label: "Fin de travaux", type: "info" },
  facturation: { label: "Facturation", type: "warning" },
  disponible: { label: "Disponible", type: "info" },
  restitue: { label: "Restitué", type: "default" },
};

// --- Components ---

function StatCard({ label, value, icon, color, loading }: {
  label: string;
  value: number;
  icon: string;
  color: string;
  loading?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 bg-white border rounded-xl p-4 flex-1 min-w-140 shadow-lg relative overflow-hidden">
      {loading && <LoadingBar variant="absolute" />}
      <div className="flex flex-row items-center justify-between">
        <div className="text-h3 text-neutral-400">{label}</div>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: color + "15" }}
        >
          <Icon1 name={icon} size={18} color={color} />
        </div>
      </div>
      <div className="text-2xl font-semibold text-black">{value}</div>
    </div>
  );
}

function StatusBadge({ label, type }: { label: string; type: "default" | "info" | "warning" }) {
  const styles = {
    default: "bg-neutral-100 text-neutral-500",
    info: "bg-blue-50 text-blue-500",
    warning: "bg-amber-50 text-amber-600",
  };
  return (
    <span className={`text-h3 px-3 py-1 rounded-full ${styles[type]}`}>
      {label}
    </span>
  );
}

function PaymentsChart() {
  const max = Math.max(...mockPaymentsData);
  const chartW = 580;
  const chartH = 180;
  const padL = 0;
  const padR = 0;

  const points = mockPaymentsData.map((v, i) => ({
    x: padL + (i / (mockPaymentsData.length - 1)) * (chartW - padL - padR),
    y: chartH - (v / max) * (chartH - 20),
  }));

  // Smooth curve
  const pathD = points.reduce((acc, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = points[i - 1];
    const cpx = (prev.x + p.x) / 2;
    return acc + ` C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
  }, "");

  // Fill area
  const fillD = `${pathD} L ${points[points.length - 1].x} ${chartH} L ${points[0].x} ${chartH} Z`;

  const yLabels = [0, 10, 20, 30, 40, 50, 60];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center">
        <svg width={chartW + 40} height={chartH + 30} className="overflow-visible">
          <defs>
            <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366F1" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#6366F1" stopOpacity="0.01" />
            </linearGradient>
          </defs>
          {/* Grid lines */}
          {yLabels.map((label) => {
            const y = chartH - (label / 60) * (chartH - 20);
            return (
              <g key={label}>
                <line x1={padL} y1={y} x2={chartW} y2={y} stroke="#f0f0f0" strokeWidth={1} />
                <text x={chartW + 8} y={y + 4} fontSize={11} fill="#a3a3a3">{label}k</text>
              </g>
            );
          })}
          {/* Fill */}
          <path d={fillD} fill="url(#chartFill)" />
          {/* Line */}
          <path d={pathD} fill="none" stroke="#6366F1" strokeWidth={2.5} />
          {/* Dot on OCT (index 9) */}
          <circle cx={points[9].x} cy={points[9].y} r={5} fill="#6366F1" stroke="white" strokeWidth={2} />
          {/* Tooltip */}
          <g transform={`translate(${points[9].x - 55}, ${points[9].y - 50})`}>
            <rect width={110} height={40} rx={8} fill="#1a1a1a" />
            <text x={55} y={16} textAnchor="middle" fontSize={11} fill="#a3a3a3">841 ventes</text>
            <text x={55} y={32} textAnchor="middle" fontSize={13} fill="white" fontWeight="600">48 400,00 €</text>
          </g>
          {/* X labels */}
          {months.map((m, i) => (
            <text
              key={m}
              x={padL + (i / (months.length - 1)) * (chartW - padL - padR)}
              y={chartH + 20}
              textAnchor="middle"
              fontSize={11}
              fill="#a3a3a3"
            >
              {m}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
}

// --- Main Screen ---

export default function HomeScreen() {
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);
  const [widgetOpen, setWidgetOpen] = useState(false);

  // Fetch ordres + vehicles on mount
  const fetchOrdres = useOrdreStore((s) => s.fetchOrdres);
  const ordres = useOrdreStore((s) => s.ordres);
  const ordresRefreshing = useOrdreStore((s) => s.isRefreshing);
  const fetchVehicles = useVehiculesStore((s) => s.fetchVehicles);
  const vehicles = useVehiculesStore((s) => s.vehicles);
  const vehiclesRefreshing = useVehiculesStore((s) => s.isRefreshing);
  const statsLoading = ordresRefreshing || vehiclesRefreshing;

  // Messagerie
  const discussions = useMessagerieStore((s) => s.discussions);
  const initGlobal = useMessagerieStore((s) => s.initGlobal);

  useEffect(() => {
    if (orgData?.id) {
      fetchOrdres(orgData.id);
      fetchVehicles(orgData.id);
    }
    initGlobal();
  }, [orgData?.id]);

  // Compute stats from real data
  const stats = useMemo(() => {
    const total = ordres.length;
    const enCours = ordres.filter((o) => o.status === "en_cours").length;
    const prepa = ordres.filter((o) => o.status === "a_preparer" || o.status === "commande" || o.status === "pret").length;
    const restitues = ordres.filter((o) => o.status === "restitue").length;
    const bloques = ordres.filter((o) => o.status === "disponible" || o.status === "fin_de_travaux").length;
    const vehicleCount = vehicles.length;

    return [
      { label: "Ordres", value: total, icon: "document", color: "#6366F1" },
      { label: "Prépa.", value: prepa, icon: "restart", color: "#6366F1" },
      { label: "En cours", value: enCours, icon: "restart", color: "#0EA5E9" },
      { label: "Restitués", value: restitues, icon: "done2", color: "#22C55E" },
      { label: "Bloqués", value: bloques, icon: "warning", color: "#F43F5E" },
      { label: "Véhicules", value: vehicleCount, icon: "car", color: "#6366F1" },
    ];
  }, [ordres, vehicles]);

  // Rating stats
  const ratingStats = useMemo(() => {
    const rated = ordres.filter((o) => o.client_rating != null && o.client_rating > 0);
    if (rated.length === 0) return null;
    const avg = rated.reduce((sum, o) => sum + (o.client_rating || 0), 0) / rated.length;
    const lowRatings = rated.filter((o) => (o.client_rating || 0) <= 2).slice(0, 3);
    return { avg: Math.round(avg * 10) / 10, count: rated.length, lowRatings };
  }, [ordres]);

  // Recent ordres (last 5)
  const recentOrdres = useMemo(() => ordres.slice(0, 5), [ordres]);

  // Recent discussions (last 4)
  const recentDiscussions = useMemo(
    () => discussions.slice(0, 4),
    [discussions],
  );

  return (
    <div className="flex flex-col overflow-auto max-h-screen relative">
      <div className="flex flex-col h-fit pb-96 pt-10 px-4 lg:px-10">
        <div className="flex flex-col w-full max-w-1k gap-6">
          {/* Header */}
          <div className="flex flex-col">
            <Text1>{`Bonjour${userData?.infos.display_name ? `, ${userData.infos.display_name}` : ""}`}</Text1>
            <Text2>Voici un aperçu de votre activité.</Text2>
          </div>

          {/* Stat cards */}
          <div className="flex flex-row gap-3 flex-wrap">
            {stats.map((stat) => (
              <StatCard key={stat.label} {...stat} loading={statsLoading} />
            ))}
          </div>

          {/* Main content: 2 columns */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Left column */}
            <div className="flex flex-col flex-1 gap-4">
              {/* Recent orders */}
              <div className="flex flex-col bg-white border rounded-xl p-4 gap-2 shadow-lg">
                <div className="flex flex-row justify-between items-center mb-2">
                  <div className="font-semibold text-black">Ordres récents</div>
                  <button
                    onClick={() => navigateTo({ route: "/ordres" })}
                    className="text-h3 text-neutral-400 hover:text-neutral-600"
                  >
                    Voir tout
                  </button>
                </div>
                <div className="flex flex-col divide-y">
                  {recentOrdres.length === 0 && (
                    <div className="text-h3 text-neutral-300 py-3 px-2">Aucun ordre</div>
                  )}
                  {recentOrdres.map((order) => {
                    const statusInfo = STATUS_LABELS[order.status] || { label: order.status, type: "default" as const };
                    return (
                      <div
                        key={order.id}
                        className="flex flex-row items-center py-3 px-2 gap-3 cursor-pointer hover:bg-neutral-50 rounded-lg"
                        onClick={() => navigateTo({ route: `/ordres/${order.id}` })}
                      >
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="text-h2 font-medium text-black">{order.name}</div>
                          <div className="text-h3 text-neutral-400">{order.immatriculation}</div>
                        </div>
                        <StatusBadge label={statusInfo.label} type={statusInfo.type} />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Payments */}
              <div className="flex flex-col bg-white border rounded-xl p-4 gap-2 shadow-lg">
                <div className="flex flex-row justify-between items-center mb-2">
                  <div className="flex flex-col">
                    <div className="font-semibold text-black">Paiements reçus</div>
                    <div className="text-hx font-semibold text-indigo-500">845 450,00 €</div>
                  </div>
                  <button className="text-h3 text-neutral-400 hover:text-neutral-600">
                    Voir tout
                  </button>
                </div>
                <PaymentsChart />
              </div>
            </div>

            {/* Right column */}
            <div className="flex flex-col w-full lg:w-300 gap-4">
              {/* Campaigns */}
              <div className="flex flex-col bg-white border rounded-xl p-4 gap-2 shadow-lg">
                <div className="flex flex-row justify-between items-center mb-1">
                  <div className="font-semibold text-black">Campagnes</div>
                  <button className="text-h3 text-neutral-400 hover:text-neutral-600">
                    Voir tout
                  </button>
                </div>
                {mockCampaigns.map((c) => (
                  <div key={c.id} className="flex flex-col p-2 bg-neutral-50 rounded-lg gap-1">
                    <div className="text-h2 font-semibold text-black">{c.title}</div>
                    <div className="text-h3 text-neutral-400">{c.description}</div>
                  </div>
                ))}
              </div>

              {/* Satisfaction */}
              {ratingStats && (
                <div className="flex flex-col bg-white border rounded-xl p-4 gap-3 shadow-lg">
                  <div className="flex flex-row justify-between items-center">
                    <div className="font-semibold text-black">Satisfaction client</div>
                    <button
                      onClick={() => navigateTo({ route: "/settings?page=avis" })}
                      className="text-h3 text-neutral-400 hover:text-neutral-600"
                    >
                      Voir tout
                    </button>
                  </div>
                  <div className="flex flex-row items-center gap-3">
                    <div className="flex flex-col items-center justify-center w-16 h-16 rounded-xl" style={{ background: ratingStats.avg >= 4 ? "#F0FDF4" : ratingStats.avg >= 3 ? "#FFFBEB" : "#FEF2F2" }}>
                      <span className="text-xl font-bold" style={{ color: ratingStats.avg >= 4 ? "#22C55E" : ratingStats.avg >= 3 ? "#F59E0B" : "#EF4444" }}>{ratingStats.avg}</span>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <svg key={s} width="8" height="8" viewBox="0 0 24 24" fill={s <= Math.round(ratingStats.avg) ? (ratingStats.avg >= 4 ? "#22C55E" : ratingStats.avg >= 3 ? "#F59E0B" : "#EF4444") : "#E5E5E5"}>
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-h2 font-medium text-black">{ratingStats.count} avis</span>
                      <span className="text-h3 text-neutral-400">ce mois</span>
                    </div>
                  </div>
                  {ratingStats.lowRatings.length > 0 && (
                    <div className="flex flex-col gap-1 border-t pt-2">
                      <span className="text-h4 text-neutral-400">Avis négatifs récents</span>
                      {ratingStats.lowRatings.map((o) => (
                        <div
                          key={o.id}
                          className="flex flex-row items-center justify-between py-1 px-1 rounded hover:bg-neutral-50 cursor-pointer"
                          onClick={() => navigateTo({ route: `/ordres/${o.id}` })}
                        >
                          <span className="text-h3 text-black">{o.name}</span>
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <svg key={s} width="10" height="10" viewBox="0 0 24 24" fill={s <= (o.client_rating || 0) ? "#EF4444" : "#E5E5E5"}>
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Recent messages */}
              <div className="flex flex-col bg-white border rounded-xl p-4 gap-2 shadow-lg">
                <div className="flex flex-row justify-between items-center mb-1">
                  <div className="font-semibold text-black">Messages récents</div>
                  <button
                    onClick={() => navigateTo({ route: "/messagerie" })}
                    className="text-h3 text-neutral-400 hover:text-neutral-600"
                  >
                    Voir tout
                  </button>
                </div>
                {recentDiscussions.length === 0 && (
                  <div className="text-h3 text-neutral-300 py-2">Aucun message</div>
                )}
                {recentDiscussions.map((disc) => (
                  <div
                    key={disc.id}
                    className="flex flex-row items-start p-2 gap-3 cursor-pointer hover:bg-neutral-50 rounded-lg"
                    onClick={() => navigateTo({ route: "/messagerie" })}
                  >
                    <div className="flex flex-col flex-1 min-w-0">
                      <div className="text-h2 font-medium text-black">{disc.name}</div>
                      <div className="text-h3 text-neutral-400 truncate">{disc.lastMessage || ""}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <div className="text-h4 text-neutral-400">{disc.lastMessageTime || ""}</div>
                      {disc.unread && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating widget button + overlay */}
      {widgetOpen ? (
        <div className="fixed bottom-6 right-6 z-50">
          <BookingWidget onClose={() => setWidgetOpen(false)} onSuccess={() => {
            if (orgData?.id) {
              // Reset refresh guard so stores refetch
              useOrdreStore.setState({ isRefreshing: false });
              useVehiculesStore.setState({ isRefreshing: false });
              fetchOrdres(orgData.id);
              fetchVehicles(orgData.id);
            }
          }} />
        </div>
      ) : (
        <button
          onClick={() => setWidgetOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full px-5 py-3 shadow-xl hover:scale-105 transition-transform"
          style={{ background: "#000000" }}
        >
          <Icon1 name="cloche" size={20} color="#FFFFFF" />
          <span className="text-h2 font-medium text-white">Widget RDV</span>
        </button>
      )}
    </div>
  );
}
